import React, { useState, useEffect } from "react";
import {
  X,
  Mail,
  Phone,
  Building2,
  User,
  MapPin,
  Calendar,
  CheckCircle,
  XCircle,
  DollarSign,
  Users,
} from "lucide-react";
import apiService from "../services/api";
import BookAppointmentModal from "./BookAppointmentModal";
import ConvertToOpportunityModal from "./ConvertToOpportunityModal";
import { useTheme } from "../contexts/ThemeContext";

const LeadDetailsModal = ({ lead, onClose, onUpdate, onDelete }) => {
  const { isDarkMode } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ ...lead });
  const [showBookAppointment, setShowBookAppointment] = useState(false);
  const [showConvertToOpportunity, setShowConvertToOpportunity] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userTeams, setUserTeams] = useState([]);

  const fetchTeamMembers = async () => {
    try {
      // First, get the user's teams
      const teamsResponse = await apiService.getUserTeams();
      const teams = teamsResponse.teams || [];
      setUserTeams(teams);

      if (teams.length === 0) {
        console.warn("User is not a member of any team");
        return;
      }

      // Collect all unique team members from all teams the user belongs to
      const allMembers = new Map();

      for (const team of teams) {
        try {
          const membersResponse = await apiService.getTeamMembers(team.id);
          const members = membersResponse.members || [];

          members.forEach((member) => {
            if (member.user && member.status === "active") {
              allMembers.set(member.user.id, {
                id: member.user.id,
                name: member.user.name,
                email: member.user.email,
                profileImageUrl: member.user.profileImageUrl,
                role: member.user.role,
              });
            }
          });
        } catch (err) {
          console.error(`Error fetching members for team ${team.id}:`, err);
        }
      }

      setUsers(Array.from(allMembers.values()));
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };

  // Update the useEffect
  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    await onUpdate(lead.id, formData);
    setIsEditing(false);
    setLoading(false);
  };

  const handleAssign = async (userId) => {
    setLoading(true);
    try {
      await apiService.assignLead(lead.id, userId);
      await onUpdate(lead.id, { ...formData, assignedTo: userId });
    } catch (error) {
      console.error("Error assigning lead:", error);
      alert("Failed to assign lead");
    }
    setLoading(false);
  };

  const handleQualify = async () => {
    setLoading(true);
    try {
      await apiService.qualifyLead(lead.id, {
        expectedRevenue: formData.expectedRevenue,
        leadTemperature: formData.leadTemperature,
      });
      await onUpdate(lead.id, formData);
    } catch (error) {
      console.error("Error qualifying lead:", error);
    }
    setLoading(false);
  };

  const handleDisqualify = async () => {
    const reason = prompt("Please provide a reason for disqualification:");
    if (!reason) return;

    setLoading(true);
    try {
      await apiService.disqualifyLead(lead.id, reason);
      await onUpdate(lead.id, formData);
    } catch (error) {
      console.error("Error disqualifying lead:", error);
    }
    setLoading(false);
  };

  const handleAppointmentBooked = (appointment) => {
    setShowBookAppointment(false);
    onUpdate(lead.id, formData);
  };

  const handleConvertedToOpportunity = (opportunity) => {
    setShowConvertToOpportunity(false);
    onClose();
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      contacted: "text-orange-400 bg-orange-400/10 border-orange-400/20",
      qualified: "text-green-400 bg-green-400/10 border-green-400/20",
      unqualified: "text-gray-400 bg-gray-400/10 border-gray-400/20",
      converted: "text-purple-400 bg-purple-400/10 border-purple-400/20",
      lost: "text-red-400 bg-red-400/10 border-red-400/20",
    };
    return colors[status] || "text-gray-400 bg-gray-400/10 border-gray-400/20";
  };

  const getTemperatureColor = (temp) => {
    const colors = {
      hot: "text-red-400 bg-red-400/10 border-red-400/20",
      warm: "text-orange-400 bg-orange-400/10 border-orange-400/20",
      cold: "text-blue-400 bg-blue-400/10 border-blue-400/20",
    };
    return colors[temp] || "text-gray-400 bg-gray-400/10 border-gray-400/20";
  };

  const canConvert =
    lead.qualificationStatus === "qualified" && lead.status !== "converted";
  const canQualify =
    lead.qualificationStatus !== "qualified" && lead.status !== "converted";

  const assignedUser = users.find((u) => u.id === lead.assignedTo);

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div
          className={`border rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-white border-gray-300"
          }`}
        >
          <div
            className={`flex items-center justify-between p-6 border-b ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <h2
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Lead Details
            </h2>
            <button
              onClick={onClose}
              className={`transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            {/* Status Badges */}
            <div className="flex flex-wrap gap-2 mb-6">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(
                  lead.status,
                )}`}
              >
                {lead.status}
              </span>
              {lead.leadTemperature && (
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full border text-sm font-medium ${getTemperatureColor(
                    lead.leadTemperature,
                  )}`}
                >
                  {lead.leadTemperature}
                </span>
              )}
              {lead.qualificationStatus && (
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full border text-sm font-medium ${
                    lead.qualificationStatus === "qualified"
                      ? "text-green-400 bg-green-400/10 border-green-400/20"
                      : lead.qualificationStatus === "disqualified"
                        ? "text-red-400 bg-red-400/10 border-red-400/20"
                        : "text-gray-400 bg-gray-400/10 border-gray-400/20"
                  }`}
                >
                  {lead.qualificationStatus}
                </span>
              )}
            </div>

            {/* Assignment Section */}
            <div
              className={`mb-6 rounded-lg p-4 border ${
                isDarkMode
                  ? "bg-gray-800/50 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users
                    size={18}
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  />
                  <h3
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Assigned To
                  </h3>
                </div>
                {!isEditing && assignedUser && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className={`text-xs transition-colors ${
                      isDarkMode
                        ? "text-blue-400 hover:text-blue-300"
                        : "text-blue-600 hover:text-blue-700"
                    }`}
                  >
                    Reassign
                  </button>
                )}
              </div>

              {isEditing ? (
                <div className="flex items-center gap-2">
                  <select
                    value={formData.assignedTo || ""}
                    onChange={(e) => handleAssign(e.target.value)}
                    disabled={loading}
                    className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#13161B] border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select user...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} {user.email && `(${user.email})`}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setIsEditing(false)}
                    className={`px-3 py-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  {assignedUser ? (
                    <>
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                          isDarkMode
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {assignedUser.profileImageUrl ? (
                          <img
                            src={assignedUser.profileImageUrl}
                            alt={assignedUser.name}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          assignedUser.name?.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <div
                          className={`font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {assignedUser.name}
                        </div>
                        <div
                          className={`text-sm ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {assignedUser.email}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Not assigned
                      <button
                        onClick={() => setIsEditing(true)}
                        className={`ml-2 transition-colors ${
                          isDarkMode
                            ? "text-blue-400 hover:text-blue-300"
                            : "text-blue-600 hover:text-blue-700"
                        }`}
                      >
                        Assign now
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mb-6">
              <button
                onClick={() => setShowBookAppointment(true)}
                disabled={lead.status === "converted"}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
                }`}
              >
                <Calendar size={16} />
                Book Appointment
              </button>

              {canQualify && (
                <button
                  onClick={handleQualify}
                  disabled={loading}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                    isDarkMode
                      ? "bg-green-600 hover:bg-green-700 text-white"
                      : "bg-green-500 hover:bg-green-600 text-white"
                  }`}
                >
                  <CheckCircle size={16} />
                  Qualify Lead
                </button>
              )}

              {canQualify && (
                <button
                  onClick={handleDisqualify}
                  disabled={loading}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                    isDarkMode
                      ? "bg-red-600/10 border border-red-600/30 text-red-400 hover:bg-red-600/20"
                      : "bg-red-50 border border-red-200 text-red-600 hover:bg-red-100"
                  }`}
                >
                  <XCircle size={16} />
                  Disqualify
                </button>
              )}

              {canConvert && (
                <button
                  onClick={() => setShowConvertToOpportunity(true)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isDarkMode
                      ? "bg-purple-600 hover:bg-purple-700 text-white"
                      : "bg-purple-500 hover:bg-purple-600 text-white"
                  }`}
                >
                  <DollarSign size={16} />
                  Convert to Opportunity
                </button>
              )}
            </div>

            {/* Contact Info */}
            <div className="mb-6">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <User
                    className={`mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    size={18}
                  />
                  <div className="flex-1">
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Full Name
                    </p>
                    <p
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {lead.fullName || "N/A"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail
                    className={`mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    size={18}
                  />
                  <div className="flex-1">
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Email
                    </p>
                    <p
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {lead.email || "N/A"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone
                    className={`mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    size={18}
                  />
                  <div className="flex-1">
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Phone
                    </p>
                    <p
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {lead.phone || "N/A"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Building2
                    className={`mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    size={18}
                  />
                  <div className="flex-1">
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Company
                    </p>
                    <p
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {lead.companyName || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Info */}
            <div className="mb-6">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Professional Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Job Title
                  </p>
                  <p
                    className={`font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {lead.jobTitle || "N/A"}
                  </p>
                </div>
                <div>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Industry
                  </p>
                  <p
                    className={`font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {lead.companyIndustry || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Lead Management */}
            <div className="mb-6">
              <h3
                className={`text-base font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Lead Management
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Status
                  </p>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full border text-sm ${getStatusColor(
                      lead.status,
                    )}`}
                  >
                    {lead.status}
                  </span>
                </div>

                <div>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Temperature
                  </p>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full border text-sm ${getTemperatureColor(
                      lead.leadTemperature || "cold",
                    )}`}
                  >
                    {lead.leadTemperature || "cold"}
                  </span>
                </div>

                {lead.appointmentCount > 0 && (
                  <div>
                    <p
                      className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Appointments
                    </p>
                    <p
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {lead.appointmentCount}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            {lead.notes && (
              <div className="mb-6">
                <h3
                  className={`text-base font-semibold mb-4 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Notes
                </h3>
                <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                  {lead.notes}
                </p>
              </div>
            )}
          </div>

          <div
            className={`p-6 border-t flex justify-between ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <button
              onClick={() => onDelete(lead.id)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "bg-red-600/10 border border-red-600/30 text-red-400 hover:bg-red-600/20"
                  : "bg-red-50 border border-red-200 text-red-600 hover:bg-red-100"
              }`}
            >
              Delete Lead
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showBookAppointment && (
        <BookAppointmentModal
          lead={lead}
          onClose={() => setShowBookAppointment(false)}
          onSuccess={handleAppointmentBooked}
        />
      )}

      {showConvertToOpportunity && (
        <ConvertToOpportunityModal
          lead={lead}
          onClose={() => setShowConvertToOpportunity(false)}
          onSuccess={handleConvertedToOpportunity}
        />
      )}
    </>
  );
};

export default LeadDetailsModal;
